/**
 * Nested modal for displaying attachment compliance details
 * Opens on top of the main compliance agent modal
 */

import React from 'react';
import { X } from 'lucide-react';
import { AttachmentComplianceSection } from './AttachmentComplianceSection';

interface FilterResult {
  action: 'ALLOW' | 'WARN' | 'AMEND' | 'BLOCK';
  amendment?: string | null;
  internal_monologue?: string;
  reason?: string;
  resource_violations?: Record<string, unknown>;
}

interface AttachmentResult {
  file_id: number | string;
  name?: string;
  filename?: string;
  content_type?: string;
  action: 'ALLOW' | 'WARN' | 'AMEND' | 'BLOCK';
  filter_result?: FilterResult;
  reason?: string;
}

interface AttachmentComplianceModalProps {
  isOpen: boolean;
  onClose: () => void;
  attachmentResults: AttachmentResult[];
  overallAction?: string;
  amendedFileIds?: (string | number)[];
}

export const AttachmentComplianceModal: React.FC<AttachmentComplianceModalProps> = ({
  isOpen,
  onClose,
  attachmentResults,
  overallAction,
  amendedFileIds = [],
}) => {
  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60, // Higher than main modal (50)
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(900px, 95vw)',
          maxHeight: '90vh',
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        className="dark:bg-gray-900 dark:border-gray-700"
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
          className="dark:border-gray-700"
        >
          <h2 style={{ fontWeight: 700, fontSize: 18 }} className="dark:text-gray-100">
            📎 Attachment Compliance Details
          </h2>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 4,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            className="hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div
          style={{
            padding: 20,
            overflowY: 'auto',
            flex: 1,
          }}
        >
          <AttachmentComplianceSection
            attachmentResults={attachmentResults}
            overallAction={overallAction}
            amendedFileIds={amendedFileIds}
          />
        </div>
      </div>
    </div>
  );
};
